import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import type { Language } from '@models/i18n/language.model';
import { translationsClient } from '@api/api-clients';
import { API_ENDPOINTS } from '@api/api.config';
import { logger } from '@utils/logger.util';

const FALLBACK_LANGUAGE: Language = 'en';
const I18N_NAMESPACE = 'resources';

type TranslationResources = Record<string, unknown>;

/** Per-language translation cache — survives re-renders, cleared on full page reload */
const translationCache = new Map<Language, TranslationResources>();

const fetchTranslations = async (lang: Language): Promise<TranslationResources> => {
  const url = API_ENDPOINTS.TRANSLATIONS(lang);
  const response = await translationsClient.get<TranslationResources>(url);
  return response.data;
};

const getCachedTranslations = (lang: Language): TranslationResources | null => {
  const cached = translationCache.get(lang);
  if (cached) {
    logger.info(`Translations served from cache: ${lang}`);
    return cached;
  }
  return null;
};

export const loadTranslations = async (lang: Language): Promise<void> => {
  try {
    // Check cache first — avoid API call if already loaded this session
    const cached = getCachedTranslations(lang);
    const resources = cached ?? (await fetchTranslations(lang));

    // Update cache if fetched from API
    if (!cached) {
      translationCache.set(lang, resources);
      logger.info(`Translations fetched from API: ${lang}`);
    }

    i18n.addResourceBundle(lang, I18N_NAMESPACE, resources, true, true);
    logger.info(`Translations loaded for language: ${lang}`);
  } catch (error) {
    logger.error(`Failed to load translations for language: ${lang}`, error);
    throw error;
  }
};

export const initI18n = async (initialLanguage: Language): Promise<void> => {
  await i18n.use(initReactI18next).init({
    lng: initialLanguage,
    fallbackLng: FALLBACK_LANGUAGE,
    ns: [I18N_NAMESPACE],
    defaultNS: I18N_NAMESPACE,
    // Resources.json uses flat dotted keys (e.g. "StandbyId90.Text").
    // Disable the default dot-separator so keys are matched literally.
    keySeparator: false,
    nsSeparator: false,
    interpolation: {
      escapeValue: false,
    },
    resources: {},
    react: {
      useSuspense: false,
    },
  });

  await loadTranslations(initialLanguage);
};

export const switchLanguage = async (lang: Language): Promise<void> => {
  await loadTranslations(lang);
  await i18n.changeLanguage(lang);
  logger.info(`Language switched to: ${lang}`);
};

export { i18n };
